import { datadogLogs } from '@datadog/browser-logs'
import { browserCookies } from '@gousto-internal/storage-consent-web-package'
import { parsePhoneNumberFromString } from 'libphonenumber-js'

import { actionTypes } from 'actions/actionTypes'
import * as trackingKeys from 'actions/trackingKeys'
import { SOCIAL_TYPES } from 'components/SocialLinks/socialReferralHelper'
// eslint-disable-next-line import/no-unresolved
import { CHECKOUT_STEPS } from 'routes/Checkout/checkoutConfig'
import { getCurrentPaymentMethod } from 'routes/Checkout/checkoutPaymentSelectors'
import { getEmail } from 'routes/Checkout/checkoutSelectors'
import { getBasketRecipes, getPromoCode } from 'selectors/basket'
import { getRecipe } from 'selectors/menu-recipes/getRecipe'
import { getPromoChannelIdFromPromoStoreEntry } from 'selectors/promoStoreSelectors'
import { getBasketRecipeQuantity } from 'utils/basket'
import { canUseWindow } from 'utils/browserEnvironment'
import logger from 'utils/logger'

import {
  getBoxTypeValue,
  getOrderDetails,
  getCollection,
  convertRecipeIdsToHashOfQuantities,
} from './dataLayerTrackerUtils'

export const sendDataLayerEvent = (event) => {
  if (!(canUseWindow() && window.dataLayer)) {
    return
  }

  window.dataLayer.push(event)
}

const sendGA4Event = (event) => {
  if (!(canUseWindow() && window.gtag)) {
    return false
  }

  window.gtag('event', event.name, event.params)

  return true
}

const trackedPurchaseTransactionIds = new Set()

const basketRecipesToItems = ({ basketRecipes, collection, state }) =>
  Object.entries(basketRecipes?.toJS() || {}).map(([recipeId, entry]) => ({
    item_id: recipeId,
    item_name: getRecipe(recipeId)(state)?.name,
    item_category: collection,
    quantity: getBasketRecipeQuantity(entry),
  }))

const sendPurchaseEvent = (params) => {
  const transactionId = params.transaction_id

  if (transactionId) {
    if (trackedPurchaseTransactionIds.has(transactionId)) {
      return
    }

    sendGA4Event({
      name: 'purchase',
      params,
    })

    trackedPurchaseTransactionIds.add(transactionId)
  }
}

export const resetTrackedPurchaseTransactionIds = () => {
  trackedPurchaseTransactionIds.clear()
}

export const pushPromoChannelIdToDataLayer = (state) => {
  const basketPromoCode = getPromoCode(state)
  const promoChannelId = getPromoChannelIdFromPromoStoreEntry(state, basketPromoCode) ?? ''

  sendDataLayerEvent({
    event: 'set_promo_channel_id',
    promo_channel_id: promoChannelId,
  })
}

export const viewRecipe = ({ recipeUuid }, state) => {
  if (!recipeUuid) {
    return
  }

  const recipe = getRecipe(recipeUuid)(state)
  if (!recipe) {
    return
  }

  sendGA4Event({
    name: 'view_item',
    params: {
      items: [
        {
          item_id: recipe.id,
          item_name: recipe.name,
          item_category: getCollection(state),
          quantity: 1,
        },
      ],
    },
  })
}

export const viewRecipeList = (_, state) => {
  const boxType = getBoxTypeValue(state)

  sendGA4Event({
    name: 'view_item_list',
    params: {
      box_type: boxType,
      items: [],
    },
  })
}

export const addRecipeToBasket = ({ recipeId: recipeUuid, trackingData, orderId }, state) => {
  if (orderId) {
    // The action is dispatched just after a signup, but it wasn't the
    // interaction from user, so we shouldn't track that.
    return
  }

  const recipe = getRecipe(recipeUuid)(state)
  if (!recipe) {
    return
  }

  const collection = trackingData?.collection || getCollection(state)

  const boxType = getBoxTypeValue(state)

  sendGA4Event({
    name: 'add_to_cart',
    params: {
      items: [
        {
          item_id: recipeUuid,
          item_name: recipe.name,
          item_category: collection,
          quantity: 1,
        },
      ],
      box_type: boxType,
    },
  })
}

export const removeRecipeFromBasket = ({ recipeId: recipeUuid, trackingData }, state) => {
  const boxType = getBoxTypeValue(state)

  const recipe = getRecipe(recipeUuid)(state)
  if (!recipe) {
    return
  }

  const collection = trackingData?.collection || getCollection(state)

  sendGA4Event({
    name: 'remove_from_cart',
    params: {
      box_type: boxType,
      items: [
        {
          item_id: recipeUuid,
          item_name: recipe.name,
          item_category: collection,
          quantity: 1,
        },
      ],
    },
  })
}

export const viewCart = ({ show, trackingData }, state) => {
  // we don't want to track if box summary is being closed
  if (!show) {
    return
  }

  const { totalPrice } = trackingData
  const basketRecipes = getBasketRecipes(state)

  const collection = getCollection(state)

  const items = basketRecipesToItems({ basketRecipes, collection, state })

  const boxType = getBoxTypeValue(state)

  sendGA4Event({
    name: 'view_cart',
    params: {
      box_type: boxType,
      currency: 'GBP',
      value: totalPrice,
      items,
    },
  })
}

export const setEnhancedConversionUserDetails = (action, state) => {
  const email = getEmail(state)
  const rawPhone = action.phone
  let phone

  if (rawPhone) {
    const parsedPhone = parsePhoneNumberFromString(rawPhone, 'GB')
    if (parsedPhone && parsedPhone.isValid()) {
      phone = parsedPhone.number
    }
  }

  if (canUseWindow() && window.gtag) {
    window.gtag('set', 'user_data', {
      email,
      phone_number: phone,
    })
  }
}

export const addShippingInfo = ({ trackingData }, state) => {
  const { totalPrice } = trackingData
  const basketRecipes = getBasketRecipes(state)

  const collection = getCollection(state)

  const items = basketRecipesToItems({ basketRecipes, collection, state })

  const boxType = getBoxTypeValue(state)
  const promoCode = getPromoCode(state)

  sendGA4Event({
    name: 'add_shipping_info',
    params: {
      currency: 'GBP',
      value: totalPrice,
      coupon: promoCode,
      box_type: boxType,
      items,
    },
  })
}

export const addPaymentMethod = ({ trackingData }, state) => {
  const { promoCode, paymentMethod, totalPrice } = trackingData
  const basketRecipes = getBasketRecipes(state)

  const collection = getCollection(state)

  const items = basketRecipesToItems({ basketRecipes, collection, state })

  const boxType = getBoxTypeValue(state)

  sendGA4Event({
    name: 'add_payment_info',
    params: {
      currency: 'GBP',
      value: totalPrice,
      coupon: promoCode,
      payment_type: paymentMethod,
      box_type: boxType,
      items,
    },
  })
}

export const signupPurchaseCompleted = (action, state) => {
  const { orderId, basketRecipes, pricing, town } = action

  const { recipeTotalDiscounted: totalPrice, totalDiscount, promoCode } = pricing || {}

  const collection = getCollection(state)

  const items = basketRecipesToItems({ basketRecipes, collection, state })

  const boxType = getBoxTypeValue(state)
  const paymentType = getCurrentPaymentMethod(state)

  sendPurchaseEvent({
    items,
    box_type: boxType,
    transaction_id: orderId,
    value: totalPrice,
    currency: 'GBP',
    coupon: promoCode,
    coupon_value: totalDiscount,
    payment_type: paymentType,
    order_origin: 'signup',
    is_first_order: true,
    town,
  })

  pushPromoChannelIdToDataLayer(state)

  if (canUseWindow()) {
    const [, consentCookie] = browserCookies.getJSON('v1_gousto_cookie_consent')
    const consentedCategories = consentCookie?.categories || []

    datadogLogs.logger.info('Info: signup purchase event sent', {
      orderId,
      boxType,
      orderValue: totalPrice,
      paymentType,
      consent: consentedCategories,
    })
  }
}

export const customerPurchaseCompleted = ({ order }, state) => {
  const { recipeIds, totalPrice, orderId } = getOrderDetails(order, state)

  const collection = getCollection(state)

  const recipes = convertRecipeIdsToHashOfQuantities(recipeIds)

  const items = Object.entries(recipes).map(([recipeId, quantity]) => ({
    item_id: recipeId,
    item_name: getRecipe(recipeId)(state)?.name,
    item_category: collection,
    quantity,
  }))

  const boxType = getBoxTypeValue(state)

  sendPurchaseEvent({
    items,
    box_type: boxType,
    transaction_id: orderId,
    value: totalPrice,
    currency: 'GBP',
    order_origin: 'transactional',
    is_first_order: false,
  })

  pushPromoChannelIdToDataLayer(state)
}

export const referFriendLinkCopied = () => {
  const event = {
    event: 'referral_click',
    type: SOCIAL_TYPES.link,
  }
  logger.debug({ message: 'dataLayerTracker - referFriendLinkCopied', extra: event })
  sendDataLayerEvent(event)
}

export const referFriendLinkShared = (action) => {
  const { trackingData } = action
  const event = {
    event: 'referral_click',
    type: trackingData.channel,
  }
  logger.debug({ message: 'dataLayerTracker - referFriendLinkShared', extra: event })
  sendDataLayerEvent(event)
}

export const locationChange = ({ payload }, state) => {
  const { pathname } = payload
  const checkoutRegexp = new RegExp('^/check-out/(.*)')
  const match = checkoutRegexp.exec(pathname)
  if (!match) {
    return
  }
  const stepName = match[1]
  const stepIndex = CHECKOUT_STEPS.findIndex((checkoutStep) => checkoutStep.id === stepName)
  if (stepIndex === -1) {
    return
  }
  const basketRecipes = getBasketRecipes(state)

  const collection = getCollection(state)

  const items = basketRecipesToItems({ basketRecipes, collection, state })

  const boxType = getBoxTypeValue(state)

  sendGA4Event({
    name: 'checkout_step',
    params: {
      items,
      box_type: boxType,
      step: stepIndex + 1,
    },
  })
}

const callbacks = {
  [actionTypes.MENU_RECIPE_DETAIL_VISIBILITY_CHANGE]: viewRecipe,
  [actionTypes.VIEW_RECIPE_LIST]: viewRecipeList,
  [actionTypes.BASKET_RECIPE_ADD]: addRecipeToBasket,
  [actionTypes.BASKET_RECIPE_REMOVE]: removeRecipeFromBasket,
  [actionTypes.BOXSUMMARY_VISIBILITY_CHANGE]: viewCart,
  [actionTypes.CHECKOUT_CONTINUE_TO_PAYMENT]: addShippingInfo,
  [trackingKeys.clickSubmitOrder]: addPaymentMethod,
  [actionTypes.CHECKOUT_SIGNUP_SUCCESS]: signupPurchaseCompleted,
  [actionTypes.ORDER_CREATE_TRANSACTIONAL]: customerPurchaseCompleted,
  [actionTypes.REFER_FRIEND_LINK_COPIED]: referFriendLinkCopied,
  [actionTypes.REFER_FRIEND_LINK_SHARE]: referFriendLinkShared,
  // eslint-disable-next-line no-underscore-dangle
  [actionTypes.__REACT_ROUTER_LOCATION_CHANGE]: locationChange,
  [actionTypes.CHECKOUT_SET_PHONE]: setEnhancedConversionUserDetails,
}

export const dataLayerTracker = (action, state) => {
  const { type } = action
  const callback = callbacks[type]

  if (!callback) {
    return
  }
  callback(action, state)
}
